import { useMemo } from "react";
import { measureTextWidth } from "@salestrak/ui";

// ---------------------------------------------------------------------------
// The card's text-mode label rail is sized per VIEW, not per row: the widest
// field/property name a view can show sets one gutter, so values start on
// the same x down every card of the board (every card in a view shows the
// same names — a value-less property just skips its row). The rail is
// capped at half the card; only a name past the cap truncates (and reads
// out on hover). A per-row hug raggeds the gutter; a flat percentage either
// wastes width on short names or clips long ones.
// ---------------------------------------------------------------------------

/** Rail cap — the value segment always keeps at least half the card. A
 *  container-query length, resolved against the card ROW
 *  ({@link CARD_ROW_CONTAINER_CLASS}), never a percentage: the token hugs
 *  its content, so a `%` would resolve against the token's own (content-
 *  derived) width and every label would clip to half of whatever its row
 *  happened to hold — the zigzag. */
export const CARD_LABEL_RAIL_CAP = "50cqi";

/** Every card body row is the inline-size query container the rail cap
 *  resolves against. On the row, not the card: the row is the width a
 *  token can take, and it stays the same for every row of a card. */
export const CARD_ROW_CONTAINER_CLASS = "@container/card-row";

/** The label's type: `Text size="xs"` (12px on the xs rung's +0.01em). */
const LABEL_FONT_SIZE_PX = 12;
const LABEL_LETTER_SPACING_PX = 0.12;
/** Label segment inset — `paddingX="sm"` on each side. */
const LABEL_INSET_PX = 8;

/** The rendered width of `text` at the label's type, in px. */
function measureLabelText(text: string): number {
  return measureTextWidth(text, {
    sizePx: LABEL_FONT_SIZE_PX,
    letterSpacingPx: LABEL_LETTER_SPACING_PX,
  });
}

/** Rail width in px for a view that can show `labels`: the widest name plus
 *  the segment's inset, rounded up so subpixel widths never ellipsize. */
export function cardLabelRailWidth(labels: readonly string[]): number {
  let widest = 0;
  for (const label of labels) widest = Math.max(widest, measureLabelText(label));
  return Math.ceil(widest) + LABEL_INSET_PX * 2;
}

/** {@link cardLabelRailWidth}, memoized on the label SET: callers hand in a
 *  fresh array each render, so the joined names are the dependency. */
export function useCardLabelRail(labels: readonly string[]): number {
  const key = labels.join("\n");
  return useMemo(() => cardLabelRailWidth(key ? key.split("\n") : []), [key]);
}
